import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Pencil, Save, ShieldAlert, Tags, X } from "lucide-react";
import { useCorporate } from "../../CorporateContext";
import { updateCorporate } from "../../../../../Services/adminCorporates";
import { Button } from "../../../../../ui/button";
import { Switch } from "../../../../../ui/switch";
import { Textarea } from "../../../../../ui/textarea";
import { Card, CardContent } from "../../../../../ui/card";
import { extractApiError } from "./utils";

/**
 * Header strip for the Benefits tab. Owns:
 *   - master switch (corporate.benefits_enabled)
 *   - governance note edit (corporate.benefits_governance_note)
 *   - signal-catalog side-panel trigger (passes click up to parent)
 *
 * Both fields live on the Corporate record itself — they're edited via the
 * existing PATCH /api/admin/enterprises/{id} endpoint that updateCorporate
 * already wraps.
 */
const BenefitsHeader = ({ onOpenSignalCatalog }) => {
  const { id } = useParams();
  const { corporate, refetch } = useCorporate();

  const [toggleBusy, setToggleBusy] = useState(false);
  const [toggleError, setToggleError] = useState(null);

  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState(null);

  useEffect(() => {
    setNoteDraft(corporate?.benefits_governance_note || "");
    setEditingNote(false);
  }, [corporate?.id, corporate?.benefits_governance_note]);

  if (!corporate) return null;

  const enabled = corporate.benefits_enabled === true;

  const handleToggle = async (next) => {
    setToggleBusy(true);
    setToggleError(null);
    try {
      await updateCorporate(id, { benefits_enabled: !!next });
      await refetch();
    } catch (err) {
      setToggleError(extractApiError(err, "Failed to update master switch."));
    } finally {
      setToggleBusy(false);
    }
  };

  const handleSaveNote = async () => {
    setNoteBusy(true);
    setNoteError(null);
    try {
      const trimmed = (noteDraft || "").trim();
      await updateCorporate(id, {
        benefits_governance_note: trimmed || null,
      });
      await refetch();
      setEditingNote(false);
    } catch (err) {
      setNoteError(extractApiError(err, "Failed to update governance note."));
    } finally {
      setNoteBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold leading-none">
                Benefits Intelligence
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Curate the benefits ARIS may surface during conversations.
                Author corporate-owned benefits, opt in to globals, and tune
                signals — privacy guarantees are enforced server-side.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenSignalCatalog}
              >
                <Tags className="h-4 w-4 mr-1.5" /> Signal catalog
              </Button>
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Switch
                  checked={enabled}
                  disabled={toggleBusy}
                  onCheckedChange={handleToggle}
                  aria-label="Benefits enabled"
                />
                <span className="text-sm font-medium">
                  {enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>

          {toggleError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {toggleError}
            </div>
          )}

          <div className="rounded-md border bg-muted/30 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium">Governance note</p>
              {!editingNote && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingNote(true)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Shown in the tab header. Use it to remind admins about your
              corporate's trust posture (e.g. "We do not see your conversations").
            </p>
            {editingNote ? (
              <div className="grid gap-2 mt-2">
                <Textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="We do not see your conversations…"
                />
                {noteError && (
                  <p className="text-xs text-destructive">{noteError}</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setNoteDraft(corporate.benefits_governance_note || "");
                      setEditingNote(false);
                      setNoteError(null);
                    }}
                    disabled={noteBusy}
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveNote}
                    disabled={noteBusy}
                  >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {noteBusy ? "Saving…" : "Save note"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm mt-2 whitespace-pre-line">
                {corporate.benefits_governance_note?.trim() ? (
                  corporate.benefits_governance_note
                ) : (
                  <span className="text-muted-foreground italic">
                    No governance note set.
                  </span>
                )}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {!enabled && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0" />
          <div className="text-sm text-yellow-900">
            <p className="font-medium">Benefits are currently disabled.</p>
            <p className="mt-0.5">
              Enable to allow ARIS to surface benefits during conversations.
              You can still author and edit drafts below, but the
              <span className="font-medium"> Activate </span>
              control is locked until benefits are enabled.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitsHeader;
